/**
 * Compile-time configuration for the dashboard.
 *
 * All values come from Vite env vars (read at build time). No production
 * URL or identifier is hardcoded in source — to deploy to a new project
 * just set the env vars in your hosting provider's config.
 *
 * `VITE_SUPABASE_URL` and `VITE_WIDGET_SCRIPT_URL` must be defined in every
 * environment — locally via dashboard/.env.local (gitignored), in production
 * via the hosting provider's env vars. There are intentionally no hardcoded
 * fallbacks (a missing env var WILL be obvious in the UI because every API
 * call fails with a clear error, and `reportConfig()` logs it).
 *
 * Env var lifecycle
 *   • Vite inlines `import.meta.env.VITE_*` at build time. Changing an
 *     env var requires rebuilding (`npm run build`) or restarting the
 *     dev server.
 *   • Vercel: set them in Project Settings → Environment Variables. They
 *     apply to all deployments without code changes.
 */

const PROD_REQUIRED: Array<[keyof ImportMetaEnv, string]> = [
  ["VITE_SUPABASE_URL", "the Supabase project URL"],
  ["VITE_WIDGET_SCRIPT_URL", "the widget bundle URL"],
];

function readEnv(name: keyof ImportMetaEnv, fallback?: string): string {
  const value = import.meta.env[name];
  if (typeof value === "string" && value.trim().length > 0) return value.trim();
  if (fallback !== undefined) return fallback;
  // In dev we let missing values fall through to an empty string — the
  // app will surface the error at the call site, which is easier to debug
  // than a startup crash. In prod, callers should check before relying.
  return "";
}

// No hardcoded fallbacks — dev and prod both read their URLs from env vars.
// For local dev, set them in dashboard/.env.local (gitignored). The dev
// fallbacks that used to live here pointed at a real dev project; keeping
// any environment's URL in source is a production-hygiene smell, so they
// were removed. A missing value yields "" and every API call fails with a
// clear error.
export const SUPABASE_URL: string = readEnv("VITE_SUPABASE_URL");

export const WIDGET_SCRIPT_URL: string = readEnv("VITE_WIDGET_SCRIPT_URL");

/**
 * Production-time sanity check. Call this once from `main.tsx`. A missing
 * prod env var is a deploy mistake — there is no fallback anymore, so we
 * log an error (every API call will also fail with a clear message).
 */
export function reportConfig(): void {
  if (import.meta.env.PROD) {
    const missing = PROD_REQUIRED.filter(([k]) => !import.meta.env[k]).map(([k, label]) => `${k} (${label})`);
    if (missing.length > 0) {
      // eslint-disable-next-line no-console
      console.error(
        "[dashboard config] Missing required production env vars: " +
        missing.join(", ") + ". Set these in your deploy environment and rebuild.",
      );
    }
  }
}
